import React, { useEffect, useState } from 'react';
import {
  Table, TableBody, TableCell, TableHead, TableRow, Typography, Box, Paper,
  Button, IconButton, styled, tableCellClasses, alpha,
} from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowBack, Refresh } from '@mui/icons-material';
import { getUserHistory } from '../utils/api';
import Sidebar from '../components/Sidebar';

interface Transaction {
  id: number;
  game_uid: string;
  transaction_type: string;
  amount: number;
  currency: string;
  transaction_status: string;
  timestamp: string;
  game_sessions: { game_uid: string; launch_url: string; started_at: string };
  game_name:string;
}

// Styled components for modern table
const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
    fontWeight: 600,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: alpha(theme.palette.action.hover, 0.04),
  },
  '&:hover': {
    backgroundColor: alpha(theme.palette.action.selected, 0.08),
  },
  transition: 'background-color 0.2s ease-in-out',
}));

const UserHistory: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const [history, setHistory] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchHistory();
  }, [userId]);

  const fetchHistory = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getUserHistory(parseInt(userId!));
      setHistory(response.data.data);
    } catch (err) {
      console.error('Failed to fetch user history', err);
      setError('Failed to load user history. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Typography variant="h6" color="text.secondary">
          Loading user history...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h6" color="error" gutterBottom>
          {error}
        </Typography>
        <Button
          variant="contained"
          startIcon={<Refresh />}
          onClick={fetchHistory}
          sx={{ mt: 2 }}
        >
          Retry
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: '#f5f7fa' }}>
   
      <Box sx={{ flexGrow: 1, p: 3, mt: 8 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <IconButton
            onClick={() => navigate(-1)}
            sx={{ mr: 2, bgcolor: 'background.paper', boxShadow: 1 }}
          >
            <ArrowBack />
          </IconButton>
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#1a237e' }}>
            User Bet History
          </Typography>
        </Box>
        <Paper
          elevation={3}
          sx={{
            borderRadius: 2,
            overflow: 'hidden',
            bgcolor: 'background.paper',
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
          }}
        >
          <Table>
            <TableHead>
              <StyledTableRow>
        
                <StyledTableCell>Game</StyledTableCell>
                <StyledTableCell>Type</StyledTableCell>
                <StyledTableCell>Amount</StyledTableCell>
                <StyledTableCell>Currency</StyledTableCell>
                <StyledTableCell>Timestamp</StyledTableCell>
              </StyledTableRow>
            </TableHead>
            <TableBody>
              {history.map((transaction) => (
                <StyledTableRow key={transaction.id}>
                
                  <StyledTableCell>{transaction.game_name}</StyledTableCell>
                  <StyledTableCell>{transaction.transaction_type}</StyledTableCell>
                  <StyledTableCell>{transaction.amount}</StyledTableCell>
                  <StyledTableCell>{transaction.currency}</StyledTableCell>
                  <StyledTableCell>
                    {new Date(transaction.timestamp).toLocaleString()}
                  </StyledTableCell>
                </StyledTableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      </Box>
    </Box>
  );
};

export default UserHistory;